import { Box, Stack, Tooltip, Typography, styled } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { Context, Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { activityLogEndpoints } from 'src/api/activity-log'
import { AppRoutesNavigation } from 'src/app-routes'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import InfiniteScrollTable from 'src/components/table/InfiniteScrollTable'
import { NavigationColumnContainer } from 'src/containers/TopBar/Index.style'
import { useNavigation } from 'src/context/NavigationContext'
import { ActivityLogOriginKey, ActivityLogReferenceKey, ActivityType } from 'src/enums'
import { IActivityLogs } from 'src/generated-types/activity-logs'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { CHECKLISTS_FEATURE_ENABLED } from 'src/hooks/useCompleteChecklistsDialog'
import { ActivityLogReportsDialog } from 'src/dialogs/ActivityLogReportsDialog'
import { activityLogQueryKey } from 'src/query-keys'
import { Flex } from 'src/styles/flexComponent'
import { getFormattedDate } from 'src/utils'
import { getCageName } from 'src/utils/function'
import NoHistoryLogs from '../../../assets/svg/no-items-common-image.svg'
import { ActivityLogEquipmentLink } from './components/ActivityLogEquipmentLink'

export interface IMainComponentHistoriesTableProps {
  customerId: EntityId
  localityId: EntityId
  equipmentLocalityId?: EntityId
  mainComponentId: EntityId
  mooringId: EntityId
  height?: number | string
  enablePagination?: boolean
  pop?: () => void
  autoHeight?: boolean
  maxHeight?: number
  maxVisibleRowCount?: number
  minimalEmptyStatus?: boolean
  onDataLengthChange?: (length: number) => void
  context?: Context<any> | null
  permission?: number
}
export const MainComponentHistoriesTable: React.FC<IMainComponentHistoriesTableProps> = ({
  customerId,
  localityId,
  equipmentLocalityId,
  mainComponentId,
  mooringId,
  height,
  pop,
  autoHeight,
  maxHeight,
  maxVisibleRowCount,
  minimalEmptyStatus = false,
  onDataLengthChange,
  context,
  permission,
}) => {
  const { t: translate } = useTranslation(['document', 'common', 'equipment', 'task'])
  const { navigateTo } = useNavigation()
  const { openFilePreviewDialog, showDialog } = useDialogCtx()
  const { localityId: currentLocalityId, serviceStationId, storageId } = useParams()
  const [dataLength, setDataLength] = useState(0)

  const openReports = (id: number) => {
    showDialog(
      ActivityLogReportsDialog,
      {
        componentProps: {
          customerId,
          activityLogId: id,
          localityId: equipmentLocalityId ?? localityId ?? (currentLocalityId as EntityId | undefined),
          serviceStationId: serviceStationId as EntityId | undefined,
          storageId: storageId as EntityId | undefined,
        },
        opts: { maxWidth: 'lg', fullWidth: true },
      },
      undefined,
      true,
    )
  }

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'activityType',
        headerName: translate('log-type', { ns: 'activity-logs' }),
        flex: 1,
        renderCell: params => translate(ActivityType[params.row?.activityType], { ns: 'activity-log-type' }),
      },
      {
        field: 'activityDescription',
        headerName: translate('activity-description', { ns: 'activity-logs' }),
        flex: 1,
        renderCell: params => translate(params.value, { ns: ['activity-log-type-description', 'activity-log-type'] }),
      },
      {
        field: 'origin',
        headerName: translate('cage', { ns: 'common' }),
        fieldSet:
          'origin,metaData,serviceStationId,localityId,mooringId,positionId,serviceStationName,localityName,positionName,positionReference',
        flex: 1,
        renderCell: params => {
          if (params.value !== null) {
            const originKey = params.value

            if (originKey == ActivityLogOriginKey.Owner && params.row?.metaData != null) {
              return (
                <Flex.Row justifyContent={'flex-start'} alignItems={'center'}>
                  <Typography variant="h6" fontSize={'14px'} fontWeight={500}>
                    {params.row?.metaData['previousOwnerName']}
                  </Typography>
                </Flex.Row>
              )
            }
            return (
              <NavigationColumnContainer
                key={'origin-key'}
                onClick={() => {
                  if (
                    pop &&
                    originKey != ActivityLogOriginKey.None &&
                    !(
                      originKey == ActivityLogOriginKey.ServiceStation &&
                      params.row?.metaData != null &&
                      params.row?.metaData['customerId'] !== null &&
                      customerId !== params.row?.metaData['customerId']
                    )
                  )
                    pop()
                  if (originKey == ActivityLogOriginKey.ServiceStation) {
                    if (params.row?.metaData != null && customerId !== params.row?.metaData['customerId']) {
                      return
                    }
                    navigateTo(
                      AppRoutesNavigation.serviceStation.serviceStation(customerId, params.row?.serviceStationId),
                    )
                  } else if (originKey == ActivityLogOriginKey.Locality) {
                    navigateTo(AppRoutesNavigation.Localities.locality(customerId, params.row?.localityId))
                  } else if (originKey == ActivityLogOriginKey.Cage) {
                    navigateTo(
                      AppRoutesNavigation.Localities.position(
                        customerId,
                        params.row?.localityId,
                        params.row?.metaData?.['mooringId'] ?? params.row?.mooringId ?? mooringId, //params.row?.mooringId contains the source mooringId
                        params.row?.positionId,
                      ),
                    )
                  } else if (originKey == ActivityLogOriginKey.ForCage) {
                    navigateTo(
                      AppRoutesNavigation.Localities.position(
                        customerId,
                        params.row?.localityId,
                        params.row?.mooringId ?? mooringId,
                        params.row?.metaData['forCageId'],
                      ),
                    )
                  } else if (originKey == ActivityLogOriginKey.FromLocality) {
                    navigateTo(
                      AppRoutesNavigation.Localities.locality(customerId, params.row?.metaData['fromLocalityId']),
                    )
                  } else if (originKey == ActivityLogOriginKey.FromStorage) {
                    navigateTo(AppRoutesNavigation.storage.storage(customerId, params.row?.metaData['fromStorageId']))
                  } else if (originKey == ActivityLogOriginKey.FromServiceStation) {
                    navigateTo(
                      AppRoutesNavigation.serviceStation.serviceStation(
                        customerId,
                        params.row?.metaData['fromServiceStationId'],
                      ),
                    )
                  }
                }}
                $active={true}
              >
                <Flex.Row justifyContent={'flex-start'} alignItems={'center'}>
                  <Typography variant="h6" fontSize={'14px'}>
                    {originKey == ActivityLogOriginKey.ServiceStation
                      ? params.row?.serviceStationName
                      : originKey == ActivityLogOriginKey.Locality
                      ? params.row?.localityName
                      : originKey == ActivityLogOriginKey.Cage
                      ? params.row?.positionName
                        ? getCageName(params.row?.positionName, params.row?.positionReference)
                        : '-'
                      : originKey == ActivityLogOriginKey.ForCage
                      ? params.row?.metaData?.forCageName
                        ? getCageName(params.row?.metaData?.forCageName, params.row?.metaData?.forCageReference)
                        : '-'
                      : originKey == ActivityLogOriginKey.FromLocality
                      ? params.row?.metaData?.['fromLocalityName']
                      : originKey == ActivityLogOriginKey.FromStorage && params.row?.metaData
                      ? params.row?.metaData?.['fromStorageName']
                      : originKey == ActivityLogOriginKey.FromServiceStation && params.row?.metaData
                      ? params.row?.metaData?.['fromServiceStationName']
                      : '-'}
                  </Typography>
                </Flex.Row>
              </NavigationColumnContainer>
            )
          } else {
            return '-'
          }
        },
      },

      {
        field: 'reference',
        headerName: translate('reference', { ns: 'activity-logs' }),
        flex: 0.75,
        fieldSet:
          'reference,metaData,serviceStationName,localityName,positionName,serviceStationId,localityId,positionId,mooringId,positionReference',
        renderCell: params => {
          if (params.value !== null) {
            const referenceKey = params.value

            // For maintenance, render as normal text without navigation
            if (referenceKey == ActivityLogReferenceKey.Maintenance && params.row?.metaData != null) {
              return (
                <Flex.Row justifyContent={'flex-start'} alignItems={'center'}>
                  <Typography variant="h6" fontSize={'14px'} fontWeight={400}>
                    {params.row?.metaData['maintenanceName']}
                  </Typography>
                </Flex.Row>
              )
            }
            if (referenceKey == ActivityLogReferenceKey.ROVVideo && params.row?.metaData != null) {
              return (
                <Flex.Row justifyContent={'flex-start'} alignItems={'center'}>
                  <DynamicTextTypography
                    text={params.row?.metaData['rovVideoTitle'] || '-'}
                    maxLength={20}
                    fontWeight={400}
                  />
                </Flex.Row>
              )
            }

            if (referenceKey == ActivityLogReferenceKey.Equipment) {
              return (
                <ActivityLogEquipmentLink
                  metaData={params.row?.metaData}
                  customerId={customerId}
                  localityId={equipmentLocalityId ?? localityId ?? (currentLocalityId as EntityId | undefined)}
                  context={context}
                  permission={permission}
                  pop={pop}
                />
              )
            }

            if (referenceKey == ActivityLogReferenceKey.Owner && params.row?.metaData != null) {
              return (
                <Flex.Row justifyContent={'flex-start'} alignItems={'center'}>
                  <Typography variant="h6" fontSize={'14px'} fontWeight={400}>
                    {params.row?.metaData['currentOwnerName']}
                  </Typography>
                </Flex.Row>
              )
            }

            return (
              <NavigationColumnContainer
                key={'meta-data-navigate-key'}
                onClick={() => {
                  if (
                    referenceKey != ActivityLogReferenceKey.Certification &&
                    referenceKey != ActivityLogReferenceKey.None &&
                    pop
                  ) {
                    pop()
                  }
                  if (referenceKey == ActivityLogReferenceKey.Deviation) {
                    if (storageId) {
                      navigateTo(
                        AppRoutesNavigation.storage.deviation(
                          customerId,
                          storageId,
                          params.row?.metaData['deviationId'],
                        ),
                      )
                    } else if (serviceStationId) {
                      navigateTo(
                        AppRoutesNavigation.serviceStation.deviation(
                          customerId,
                          serviceStationId,
                          params.row?.metaData['deviationId'],
                        ),
                      )
                    } else if (currentLocalityId) {
                      navigateTo(
                        AppRoutesNavigation.Localities.deviation(
                          customerId,
                          localityId ?? currentLocalityId,
                          params.row?.metaData['deviationId'],
                        ),
                      )
                    } else {
                      navigateTo(
                        AppRoutesNavigation.Customer.deviation(customerId, params.row?.metaData['deviationId']),
                      )
                    }
                  } else if (referenceKey == ActivityLogReferenceKey.ServiceStation) {
                    navigateTo(
                      AppRoutesNavigation.serviceStation.serviceStation(customerId, params.row?.serviceStationId),
                    )
                  } else if (referenceKey == ActivityLogReferenceKey.Locality) {
                    navigateTo(AppRoutesNavigation.Localities.locality(customerId, params.row?.localityId))
                  } else if (referenceKey == ActivityLogReferenceKey.Cage) {
                    navigateTo(
                      AppRoutesNavigation.Localities.position(
                        customerId,
                        params.row?.localityId,
                        params.row?.metaData?.['mooringId'] ?? params.row?.mooringId ?? mooringId,
                        params.row?.positionId,
                      ),
                    )
                  } else if (referenceKey == ActivityLogReferenceKey.Document) {
                    if (serviceStationId) {
                      navigateTo(
                        AppRoutesNavigation.serviceStation.document(
                          customerId,
                          serviceStationId,
                          params.row?.metaData['documentId'],
                        ),
                      )
                    } else if (storageId) {
                      navigateTo(
                        AppRoutesNavigation.storage.document(customerId, storageId, params.row?.metaData['documentId']),
                      )
                    } else if (currentLocalityId) {
                      navigateTo(
                        AppRoutesNavigation.Localities.document(
                          customerId,
                          localityId ?? currentLocalityId,
                          params.row?.metaData['documentId'],
                        ),
                      )
                    } else {
                      navigateTo(AppRoutesNavigation.Customer.document(customerId, params.row?.metaData['documentId']))
                    }
                  } else if (referenceKey == ActivityLogReferenceKey.ForCage) {
                    navigateTo(
                      AppRoutesNavigation.Localities.position(
                        customerId,
                        params.row?.localityId,
                        params.row?.metaData?.['mooringId'] ?? params.row?.mooringId ?? mooringId,
                        params.row?.metaData['forCageId'],
                      ),
                    )
                  } else if (referenceKey == ActivityLogReferenceKey.ToStorage) {
                    navigateTo(AppRoutesNavigation.storage.storage(customerId, params.row?.metaData['toStorageId']))
                  } else if (referenceKey == ActivityLogReferenceKey.Certification) {
                    openFilePreviewDialog(params.row?.metaData['certificateUrl'])
                  }
                }}
                $active={true}
              >
                <Flex.Row justifyContent={'flex-start'} alignItems={'center'}>
                  <Typography variant="h6" fontSize={'14px'}>
                    {referenceKey == ActivityLogReferenceKey.Deviation
                      ? params.row?.metaData?.deviationTitle
                      : referenceKey == ActivityLogReferenceKey.ServiceStation
                      ? params.row?.serviceStationName
                      : referenceKey == ActivityLogReferenceKey.Locality
                      ? params.row?.localityName
                      : referenceKey == ActivityLogReferenceKey.Cage
                      ? params.row?.positionName
                        ? getCageName(params.row?.positionName, params.row?.positionReference)
                        : '-'
                      : referenceKey == ActivityLogReferenceKey.Document
                      ? params.row?.metaData?.['documentTitle']
                      : referenceKey == ActivityLogReferenceKey.ForCage
                      ? params.row?.metaData?.forCageName
                        ? getCageName(params.row?.metaData?.forCageName, params.row?.metaData?.forCageReference)
                        : '-'
                      : referenceKey == ActivityLogReferenceKey.ToStorage && params.row?.metaData
                      ? params.row?.metaData?.['toStorageName']
                      : referenceKey == ActivityLogReferenceKey.Certification && params.row?.metaData
                      ? params.row?.metaData?.['certificateNumber']
                      : '-'}
                  </Typography>
                </Flex.Row>
              </NavigationColumnContainer>
            )
          } else {
            return '-'
          }
        },
      },
      {
        field: 'performedByUserName',
        headerName: translate('performedBy', { ns: 'activity-logs' }),
        flex: 1,
        renderCell: params => {
          return (
            <Fragment>
              <DynamicTextTypography text={params.row?.performedByUserName || '-'} maxLength={20} fontWeight={400} />
            </Fragment>
          )
        },
      },
      {
        field: 'logDate',
        headerName: translate('performed', { ns: 'activity-logs' }),
        flex: 1,
        renderCell: params => {
          const formattedDate = getFormattedDate(new Date(params.row?.logDate + 'Z'), 'YYYY-MM-DD, HH:mm')
          return (
            <Fragment>
              <Typography variant="body1" fontWeight={400}>
                {formattedDate}
              </Typography>
            </Fragment>
          )
        },
      },
      {
        field: 'effectiveDate',
        headerName: translate('effective-date', { ns: 'activity-logs' }),
        flex: 1,
        renderCell: params => {
          return params.row?.effectiveDate !== null
            ? getFormattedDate(new Date(params.row?.effectiveDate))
            : getFormattedDate()
        },
      },
      ...(CHECKLISTS_FEATURE_ENABLED
        ? [
            {
              field: 'hasCompletedReports',
              headerName: translate('completed-reports', { ns: 'task' }),
              flex: 1,
              renderCell: params => {
                const hasReports = params.row?.hasCompletedReports
                return (
                  <Stack>
                    {params.row?.hasCompletedReports ? (
                      <Tooltip title={translate('open', { ns: 'common' }) as string} disableHoverListener={!hasReports}>
                        <Box>
                          <CustomLink
                            text={translate('Checklists', { ns: 'checklist' })}
                            maxLength={20}
                            onClick={() => openReports(params.row?.id)}
                            disabled={!hasReports}
                            clickable={hasReports}
                          />
                        </Box>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </Stack>
                )
              },
            } as GridColumns[number],
          ]
        : []),
    ],
    [customerId, localityId, equipmentLocalityId, mooringId, context, permission, translate],
  )

  return (
    <InfiniteScrollTable<IActivityLogs>
      id="main-component-history-table-key"
      columns={columns}
      height={height}
      autoHeight={autoHeight}
      maxVisibleRowCount={maxVisibleRowCount}
      getRowHeight={() => 36}
      minRowHeight="36px"
      defaultColumnOption={columns
        .filter(c =>
          [
            'id',
            'activityType',
            'origin',
            'reference',
            'performedByUserName',
            'logDate',
            'effectiveDate',
            'actions',
          ].includes(c.field),
        )
        .map(c => ({ field: c.field, headerName: c.headerName as string }))}
      enableColumnOptions={true}
      enableToolbar={true}
      hideDefaultFooterPagination={true}
      pageSize={10}
      containerProps={{
        sx: {
          '& .MuiDataGrid-virtualScroller': {
            paddingBottom: '0px !important',
            overflowX: 'hidden',
          },
          '& .MuiDataGrid-virtualScrollerContent': {
            paddingBottom: '0px !important',
          },
        },
      }}
      onDataChange={data => {
        setDataLength(data.length)
        onDataLengthChange?.(data.length)
      }}
      noDataComponent={() => (
        <Box height="100%">
          <EmptyStatusCard
            imageWidth="107px"
            image={NoHistoryLogs}
            header={translate('activity-logs-empty status-title', { ns: 'activity-logs' })}
            content={translate('activity-logs-empty status-content', { ns: 'activity-logs' })}
            headerFontSize="18px"
            contentFontSize="14px"
            contentClasses="mt-2"
            contentTopMargin="10px"
            imageContainerClasses="pb-4 pt-4"
            height="100%"
            hideImage={minimalEmptyStatus}
            compact={minimalEmptyStatus}
          />
        </Box>
      )}
      queryUrl={activityLogEndpoints.getMainComponentActivityLogs()}
      queryParams={{
        mainComponentId: mainComponentId,
        fields: 'id,activityType,activityDescription,hasCompletedReports',
      }}
      generateQueryKey={(page: number, pageSize: number, fields?: string) =>
        activityLogQueryKey.GET_ACTIVITY_LOGS({
          mainComponentId: mainComponentId,
          page: page,
          pageSize: pageSize,
          fields: fields,
        })
      }
      spinnerHeight={100}
    />
  )
}

const CustomLink = styled(DynamicTextTypography, {
  shouldForwardProp: prop => !['hasId', 'disabled'].includes(prop as string),
})<{ clickable: boolean; disabled?: boolean }>(({ theme, clickable, disabled }) => ({
  textDecoration: clickable ? 'underline' : 'none',
  textUnderlineOffset: clickable ? '8px' : undefined,
  cursor: clickable ? 'pointer' : 'default',
  color: disabled ? theme.palette.common.black : clickable ? theme.palette.primary.light : theme.palette.common.black,
  opacity: disabled ? 0.6 : 1,
}))
